import FieldError from './FieldError';
import ObjectError from './ObjectError';
import UnknownError from './UnknownError';
import { logoutActiveSessionWithoutToken } from 'services/authUtils';
import { ROUTE_PATHS } from 'routes/constants';
import PreparedTransactionsError from './PreparedTransactionsError';

// OAuth2 error codes mapping
const OAUTH2_ERROR_CODES = {
  BAD_REQUEST: 'BAD_REQUEST',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  TOO_MANY_REQUESTS: 'TOO_MANY_REQUESTS',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
  CONFIRMATION_OUT_OF_DATE: 'CONFIRMATION_OUT_OF_DATE',
  CONFIRMATION_CODE_TRIES_EXCEED: 'CONFIRMATION_CODE_TRIES_EXCEED',
  CONFIRMATION_CODE_NOT_VALID: 'CONFIRMATION_CODE_NOT_VALID',
  CONFIRMATION_PENDING_ACTION: 'CONFIRMATION_PENDING_ACTION',
  CONFIRMATION_REJECTED: 'CONFIRMATION_REJECTED',
  CONFIRMATION_COMPLETED: 'CONFIRMATION_COMPLETED',
  CONFIRMATION_STATUS_NOT_SUPPORTED: 'CONFIRMATION_STATUS_NOT_SUPPORTED',
  CODE_SEND_TIMEOUT: 'CODE_SEND_TIMEOUT',
  OPERATION_NOT_SUPPORTED: 'OPERATION_NOT_SUPPORTED',
  MESSAGE_NOT_READABLE: 'MESSAGE_NOT_READABLE',
  ACCESS_DENIED: 'ACCESS_DENIED'
};

async function errorMapper(error, errorFieldMapper = {}, requestContext = {}) {
  // Prepared transaction errors — check before generic OAuth2 handler
  if (error?.response?.body?.code === 'PREPARED_TRANSACTION_CREATE_ERROR') {
    throw new PreparedTransactionsError({
      code: error.response.body.code,
      message: error.response.body.message,
      details: error.response.body.details
    });
  }

  // Check for OAuth2 error format (new format)
  if (error?.response?.body?.code && error?.response?.body?.message && !error?.response?.body?.fields) {
    const oauth2Code = error.response.body.code;

    // Handle authentication failures
    if (oauth2Code === OAUTH2_ERROR_CODES.ACCESS_DENIED || oauth2Code === OAUTH2_ERROR_CODES.INVALID_CREDENTIALS) {
      // Don't logout for ACCESS_DENIED on messages endpoints when user is a representative
      const isMessagesEndpoint =
        requestContext?.path?.includes('/topic/list') || requestContext?.path?.includes('/topic');
      const shouldSkipLogout = oauth2Code === OAUTH2_ERROR_CODES.ACCESS_DENIED && isMessagesEndpoint;

      if (
        !shouldSkipLogout &&
        window.location.pathname !== ROUTE_PATHS.LOGIN &&
        window.location.pathname !== ROUTE_PATHS.REGISTRATION
      ) {
        logoutActiveSessionWithoutToken();
      }
    }

    throw new UnknownError({
      status: error.status,
      code: oauth2Code,
      message: error.response.body.message,
      details: error.response.body.details
    });
  }

  // Legacy OAuth error handling
  // Check if it's a messages endpoint before logout on 403
  const isMessagesEndpoint = requestContext?.path?.includes('/topic/list') || requestContext?.path?.includes('/topic');
  const shouldSkipLogoutFor403 = error.status === 403 && isMessagesEndpoint;

  if (
    (error.message === 'Unauthorized' && error.status === 401) ||
    (error.status === 403 && !shouldSkipLogoutFor403) ||
    // eslint-disable-next-line max-len
    (['invalid_refresh_token', 'invalid_token', 'access_denied'].includes(error.response?.body?.error) &&
      window.location.pathname !== ROUTE_PATHS.LOGIN &&
      window.location.pathname !== ROUTE_PATHS.REGISTRATION)
  ) {
    logoutActiveSessionWithoutToken();
  }
  if (error?.response?.body?.fields) {
    const errorFields = error.response.body.fields.map(({ code, field }) => ({
      field: errorFieldMapper[field] ? errorFieldMapper[field] : field,
      code
    }));
    throw new FieldError({
      code: error.response.body.code,
      status: error.status,
      fields: errorFields
    });
  } else if (error?.response?.body?.args) {
    throw new ObjectError({
      status: error.status,
      code: error.response.body.code,
      args: error.response.body.args
    });
  } else {
    throw new UnknownError({
      status: error?.status,
      code: error?.response?.body?.code || error?.response?.body?.error || error?.code,
      message: error?.response?.body?.message || error?.response?.body?.error_description || error?.message,
      details: error?.response?.body?.details,
      stack: error?.stack
    });
  }
}

export { OAUTH2_ERROR_CODES };
export default errorMapper;
